import { usePermissionsHook } from "./usePermissionsHook";
import { hasGroup, useUserGroupNames } from "./useUserGroupNames";

export const USER_ACTIVITY_GROUP = "User Activity";

/**
 * Who may see the **User Activity** view on the Users screen.
 *
 * **Super admins first, always.** A super admin (usePermissionsHook's email
 * allowlist) is allowed without consulting the group at all — per CLAUDE.md
 * *Super admins bypass every permission*. No fetch, no loading state.
 *
 * Everyone else: membership, and only membership — the logged-in user must be
 * in the org group literally named "User Activity". There is deliberately **no
 * plain-admin bypass** — this is an oversight surface showing what every named
 * person did all day, so who can open it is a roster decision the desk makes by
 * adding someone to the group, not something an app-level role grants by side
 * effect.
 *
 * That roster is portal-owned and the two portals differ (EV's is the 12-member
 * elevated-user list; LQ has its own), so nothing here hardcodes names — the
 * answer comes from `GET /groups/for-user/{email}` (via `useUserGroupNames`,
 * shared with the other group gates).
 *
 * **Kept live** (see `useUserGroupNames`): removing yourself from the group
 * makes the tab disappear on the spot rather than at the next page load.
 *
 * Fails CLOSED — an errored lookup, a user with no email yet, or a list still
 * loading all return false. An oversight view is the wrong thing to open by
 * accident, and a tab that flashes in and then out is worse than one that
 * arrives a beat late.
 */
export const useIsInUserActivityGroup = () => {
  const { isSuperAdmin } = usePermissionsHook();
  const { names, loading } = useUserGroupNames({ skip: isSuperAdmin });

  if (isSuperAdmin) return { allowed: true, loading: false };

  return { allowed: hasGroup(names, USER_ACTIVITY_GROUP), loading };
};

export default useIsInUserActivityGroup;
